import generateWords from "features/authentication/assets/12words";
import Loader from "components/shared/loader/Loader";
import GuiContext from "config/context/guiContext";
import TableContext from "config/context/tableContext";
import useClipboard from "config/useClipboard";
import useCustomStyles from "config/useCustomStyles";
import useLogin from "features/authentication/hooks/useLogin";
import { useHandleFormSubmit } from "features/authentication/hooks/useRegisterHook";
import { ReactComponent as GoodIcon } from "img/green.svg";
import { ReactComponent as BadIcon } from "img/red.svg";
import { useContext, useEffect, useState } from "react";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import Button from "components/shared/button/Button";
import styles from "./RegisterModal.module.css";
import downloadFile from "lib/DownloadFile";
import PrivateKeyField from "components/shared/private-key-field/PrivateKeyField";

const RegisterModal = ({
  isOpen,
  setIsOpen,
  accountExists,
  formData,
  dark,
  widthRef,
}) => {
  const {
    handleFormSubmit,
    formDataResponse,
    formResponseError,
    isFormResponseLoading,
    resetFormState,
  } = useHandleFormSubmit();
  const [words, setWords] = useState(generateWords());
  const { writeClipboard } = useClipboard();
  const [readMore, setReadMore] = useState(false);
  const [step, setStep] = useState(1);
  const [checked, setChecked] = useState(false);
  const formDataWithWords = { ...formData, words };
  const {
    setInitTabStructAndComplex,
    setActiveComposition,
  } = useContext(TableContext);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { changeUserData, URL } = useContext(GuiContext);
  const { signIn } = useLogin(
    URL,
    navigate,
    setInitTabStructAndComplex,
    setActiveComposition,
    changeUserData,
  );
  const handleSignIn = async () => {
    setIsLoading(true);
    await signIn(formData.guiname, formData.pincode, formData.server_url);
    setIsLoading(false);
  };
  const finishingRegistration = () => {
    if (!checked) return;

    setStep(3);
    handleSignIn();
  };

  const cancelRegistration = () => {
    resetFormState();
    setStep(1);
    setIsOpen(false);
  };

  const newWords = () => {
    setWords(generateWords());
  };

  const handleExportKeys = async () => {
    if (formDataResponse?.private_key && formDataResponse?.public_key) {
      // Ključ bez naloga kome pripada je pola podatka. Ime naloga se posle
      // registracije nigde ne pamti van pregledača, a povratak baze kroz
      // „Add New Database → import" traži TAČNO to ime i baš ovaj ključ — pa
      // idu u isti fajl. Adresa čvora ide uz njih jer nalog postoji na jednom
      // lancu, ne svuda.
      const content = [
        `Blockchain account: ${formData?.account_name || ""}`,
        `GUI account name:   ${formData?.guiname || ""}`,
        `Inery node:         ${formData?.server_url || ""}`,
        "",
        `Public Key:\n${formDataResponse.public_key}`,
        "",
        `Private Key:\n${formDataResponse.private_key}`,
      ].join("\n");
      const formattedDate = new Date().toISOString().slice(0, 10);
      const safeAccountName = (formData?.account_name || "account")
        .trim()
        .replace(/[^a-zA-Z0-9._-]/g, "_");
      const filename = `${safeAccountName}_active_keys_${formattedDate}.txt`;
      const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });

      if (window.showSaveFilePicker) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: "Text files",
                accept: { "text/plain": [".txt"] },
              },
            ],
          });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (error) {
          if (error?.name === "AbortError") return;
        }
      }

      downloadFile(content, filename, { type: "text/plain;charset=utf-8;" });
    }
  };

  let configObj = {
    background: "#fff",
    darkBG: "#151515",
  };
  const customStyles = useCustomStyles(configObj);

  useEffect(() => {
    if (formDataResponse) {
      setStep(2);
      return;
    }
    if (formResponseError) {
      setStep(4);
      return;
    }
  }, [formDataResponse, formResponseError]);

  const renderModal = () => {
    if (isFormResponseLoading) {
      return (
        <div className={styles.loaderModal}>
          <Loader />
        </div>
      );
    }

    switch (step) {
      case 1:
        return (
          <>
            <em style={{ wordBreak: "keep-all" }}>
              Your 12 word combination is essential for accessing your account.
              Please write down this combination and keep it safe.
            </em>
            {widthRef.current < 1300 ? (
              <>
                {readMore && (
                  <em style={{ wordBreak: "keep-all" }}>
                    You can copy the words by clicking on the combination, or
                    you can generate a new combination if you're not satisfied
                    with the current one. Once you have written down the
                    combination, click on the "Confirm" button to continue.
                  </em>
                )}
                <Button
                  buttonType="text"
                  variant="secondary"
                  onClick={() => setReadMore((e) => !e)}
                >
                  {readMore ? " Read less" : "Read more"}
                </Button>
              </>
            ) : (
              <em style={{ wordBreak: "keep-all" }}>
                You can copy the words by clicking on the icon on the right, or
                you can generate a new combination if you're not satisfied with
                the current one. Once you have written down the combination,
                click on the "Confirm" button to continue.
              </em>
            )}
            <div className={`${styles.modalWords} row justify-center`}>
              {words.map((word, i) => (
                <span className={styles.modalWord} key={i}>
                  {word}
                </span>
              ))}
              <Button className={styles.copyButton} buttonType="outlined"
                onClick={() => writeClipboard(words.join(" "))}>
                <img
                  width={15}
                  src={`../img/copy${dark ? "-dark" : ""}.svg`}
                  alt="copy"
                />
              </Button>
            </div>
            <div className={styles.modalGroupControl}>
              <Button variant="secondary" onClick={newWords}>
                New Combination
              </Button>
              <Button onClick={() => handleFormSubmit(formDataWithWords)}>
                Confirm
              </Button>
            </div>
          </>
        );
      case 2:
        return (
          <>
            {!accountExists && (
              <>
                <p style={{ wordBreak: "keep-all" }}>
                  Public keys are used to encrypt data that is sent to you,
                  while private keys are used to decrypt that data. It is
                  important to remember that both your public and private keys
                  are equally important and should be kept safe. If you lose
                  your private key, you will no longer be able to access
                  encrypted data that was sent to you. We strongly recommend
                  that you write down both your public and private keys and
                  store them in a safe place.
                </p>
                <h4>Private key</h4>
                <div className={styles.copyBox}>
                  <PrivateKeyField
                    value={formDataResponse.private_key}
                    label
                  />
                  <Button 
                  className={styles.copyButton} 
                  buttonType="outlined"
                  onClick={()=> writeClipboard(formDataResponse?.private_key)}
                  >
                    <img
                      width={15}
                      src={`../img/copy${dark ? "-dark" : ""}.svg`}
                      alt="copy"
                    />
                  </Button>
                </div>
                <h4>Public key</h4>
                <div className={styles.copyBox}>
                  <p style={{ overflowWrap: "anywhere" }}>
                    {formDataResponse.public_key}
                  </p>
                  <Button 
                  className={styles.copyButton} 
                  buttonType="outlined"
                  onClick={()=> writeClipboard(formDataResponse?.public_key)}
                  >
                    <img
                      id="public_key"
                      width={15}
                      src={`../img/copy${dark ? "-dark" : ""}.svg`}
                      alt="copy"
                    />
                  </Button>
                </div>
                {/* Dugme, ne veza: nigde ne vodi nego pokreće snimanje
                    ključeva. Kao `<a href="#">` je ispadalo iz reda pri
                    kretanju tastaturom, a čitač ekrana ga je najavljivao kao
                    odredište. Izgled je nepromenjen — `.exportLink` gasi
                    podrazumevani okvir i pozadinu dugmeta. */}
                <button
                  type="button"
                  className={styles.exportLink}
                  onClick={handleExportKeys}
                >
                  Export keys to text file
                </button>

                <div className="column gap-1 text-center">
                  <p className="s-2" onClick={() => setChecked((e) => !e)}>
                    <input checked={checked} type="checkbox" /> I wrote down
                    private and public key
                  </p>
                  <Button
                    disabled={!checked}
                    onClick={finishingRegistration}
                    expand="full"
                  >
                    Finish
                  </Button>
                </div>
              </>
            )}
            {accountExists && (
              <div className="column align-center text-center">
                <p style={{ wordBreak: "keep-all" }}>
                  You have created a new database that now contains your
                  existing database.
                </p>
                <p className="s-2" style={{ wordBreak: "keep-all" }} onClick={() => setChecked((e) => !e)}>
                  <input checked={checked} type="checkbox" /> I wrote down
                  private and public key
                </p>
                <Button onClick={finishingRegistration} expand="full">
                  Finish
                </Button>
              </div>
            )}
          </>
        );
      case 3:
        return (
          <div className={"fade-in"}>
            {isLoading ? (
              <Loader />
            ) : (
              <div className={`${styles.container} ${styles.column}`}>
                <GoodIcon width={"50px"} alt="success" />
                <p>Your registration was successful!</p>
                {/* Jedino mesto gde korisnik vidi ime naloga posle unosa. Do
                    sada je forma to zvala „Database Name", pa se posle nigde
                    nije ponovilo — a bez njega se baza ne može vratiti kroz
                    uvoz. */}
                {formData?.account_name && (
                  <p>
                    Your blockchain account is{" "}
                    <strong>{formData.account_name}</strong>. It is also your
                    first database.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      default:
        return (
          <div className="fade-in">
            <div className={`${styles.container} ${styles.column}`}>
              <BadIcon width={"50px"} alt="wrong" />
              <p>{formResponseError}</p>
              <br />
              <Button
                onClick={cancelRegistration}
                buttonType="text"
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} style={customStyles}>
      <div className={`${styles.modalText}  ${dark ? "dark" : ""}`}>
        {renderModal()}
      </div>
    </Modal>
  );
};

export default RegisterModal;
